import React from 'react';
import apiService from '../services/api';

interface QRCodeViewerProps {
  qrCodeUrl: string;
  code: string;
  name: string;
  channel: string;
  url: string;
  onClose: () => void;
}

const QRCodeViewer: React.FC<QRCodeViewerProps> = ({
  qrCodeUrl,
  code,
  name,
  channel,
  url,
  onClose
}) => {
  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${name}-${code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Built from the API base, not window.location.origin. This dashboard's
  // origin serves index.html for every path, so a code printed with that URL
  // records no scan and shows whoever scanned it an admin login screen.
  const trackingUrl = apiService.qrTrackingUrl(code);

  const copyTrackingUrl = () => {
    navigator.clipboard.writeText(trackingUrl);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-8 border border-ice-teal-light shadow-xl max-w-md w-full mx-4">
        <div className="text-center">
          <h3 className="text-2xl font-title font-bold text-deep-navy mb-4">{name}</h3>

          {/* QR Code Image */}
          <div className="bg-frost-white rounded-2xl p-4 mb-6 border border-ice-teal-light">
            <img
              src={qrCodeUrl}
              alt={`QR Code for ${name}`}
              className="w-full h-auto"
            />
          </div>

          {/* QR Code Details. A description list rather than labels: nothing
              here is a form field, and a <label> with no control to name is an
              empty promise to a screen reader. */}
          <dl className="space-y-3 mb-6 text-left">
            <div>
              <dt className="block text-gray-500 text-sm font-bold">Channel:</dt>
              <dd className="text-deep-navy font-body">{channel}</dd>
            </div>
            <div>
              <dt className="block text-gray-500 text-sm font-bold">Code:</dt>
              <dd className="text-gray-800 font-mono text-sm">{code}</dd>
            </div>
            <div>
              <dt className="block text-gray-500 text-sm font-bold">Destination URL:</dt>
              <dd className="text-gray-800 font-mono text-sm break-all">{url}</dd>
            </div>
          </dl>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3">
            <button
              onClick={downloadQRCode}
              className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
            >
              Download QR Code
            </button>
            <button
              onClick={copyTrackingUrl}
              className="w-full bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
            >
              Copy Tracking URL
            </button>
            <button
              onClick={onClose}
              className="w-full bg-frost-white hover:bg-ice-pale text-gray-700 font-bold px-6 py-3 rounded-xl transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeViewer;
